import { useState, type DragEvent, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { useIsField } from '../../lib/media.js'
import { Icon } from '../Icon.js'
import { EcoButton } from '../eco/EcoButtons.js'
import { Modal, ModalCloseButton } from '../Modal.js'
import { Sheet } from '../Sheet.js'
import { AssistantAvatar, AssistantPanel } from './AssistantPanel.js'
import type { ComposeAssistant } from './useComposeAssistant.js'

const COLLAPSED_KEY = 'fieldops.compose.assistant'

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === 'hidden'
  } catch {
    return false
  }
}

function writeCollapsed(hidden: boolean) {
  try {
    if (hidden) window.localStorage.setItem(COLLAPSED_KEY, 'hidden')
    else window.localStorage.removeItem(COLLAPSED_KEY)
  } catch {
    // A private window without storage just forgets.
  }
}

function hasFiles(event: DragEvent) {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files')
}

/**
 * The compose window: the message on the left, the Assistant on the right.
 *
 * The Assistant can be tucked away with `>>` and stays tucked for next time.
 * In the field app there is one column, and the Assistant is a sheet over the
 * message. Files dropped anywhere on the message side are attached.
 */
export function ComposeShell({
  channel,
  title,
  heading,
  assistant,
  drop,
  onClose,
  children,
  footer,
}: {
  channel: 'EMAIL' | 'SMS'
  title: string
  heading: string | null
  /** Null when there is no writing help on this system. */
  assistant: ComposeAssistant | null
  /** Null when this message cannot carry files. */
  drop: { enabled: boolean; hint: string; onFiles: (files: File[]) => void } | null
  onClose: () => void
  children: ReactNode
  footer: ReactNode
}) {
  const field = useIsField()
  const [hidden, setHidden] = useState(readCollapsed)
  const [sheet, setSheet] = useState(false)
  const [dragging, setDragging] = useState(false)
  const titleId = channel === 'SMS' ? 'sms-compose-title' : 'email-compose-title'
  const beside = assistant !== null && !field && !hidden

  const collapse = (value: boolean) => {
    setHidden(value)
    writeCollapsed(value)
  }

  return (
    <Modal
      open
      onClose={onClose}
      labelledBy={titleId}
      className={cn(
        field ? 'h-[calc(100dvh-2rem)] max-w-[640px]' : 'h-[min(760px,calc(100dvh-2rem))]',
        !field && (beside ? 'max-w-[1040px]' : 'max-w-[700px]'),
      )}
    >
      <div className="flex min-h-0 flex-1">
        <section
          className="relative flex min-w-0 flex-1 flex-col"
          onDragEnter={(event) => {
            if (drop && hasFiles(event)) setDragging(true)
          }}
          onDragOver={(event) => {
            if (!drop || !hasFiles(event)) return
            event.preventDefault()
            event.dataTransfer.dropEffect = drop.enabled ? 'copy' : 'none'
          }}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false)
          }}
          onDrop={(event) => {
            if (!drop || !hasFiles(event)) return
            event.preventDefault()
            setDragging(false)
            if (drop.enabled) drop.onFiles(Array.from(event.dataTransfer.files))
          }}
        >
          <header className="flex items-center gap-3 border-b border-ink-100 px-5 py-3.5">
            <span
              aria-hidden="true"
              className="grid size-9 shrink-0 place-items-center rounded-lg bg-ink-100 text-ink-700 ring-1 ring-ink-200 ring-inset"
            >
              <Icon name={channel === 'SMS' ? 'sms' : 'mail'} className="size-4.5" />
            </span>
            <div className="flex min-w-0 flex-col">
              <h2 id={titleId} className="text-[16px] leading-tight font-semibold text-ink-900">
                {title}
              </h2>
              {heading ? <p className="truncate text-[13px] text-ink-500">{heading}</p> : null}
            </div>
            <div className="ml-auto flex items-center gap-1">
              {assistant && (field || hidden) ? (
                <EcoButton size="sm" onClick={() => (field ? setSheet(true) : collapse(false))}>
                  Assistant
                </EcoButton>
              ) : null}
              <ModalCloseButton onClose={onClose} />
            </div>
          </header>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 pb-4">{children}</div>

          <footer className="flex items-center gap-1 border-t border-ink-100 px-4 py-3">{footer}</footer>

          {dragging && drop ? (
            <div
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute inset-2 z-40 grid place-items-center rounded-lg border-2 border-dashed',
                drop.enabled ? 'border-brand-600 bg-white/90' : 'border-ink-300 bg-ink-50/90',
              )}
            >
              <p className={cn('text-[14px] font-semibold', drop.enabled ? 'text-ink-900' : 'text-ink-600')}>
                {drop.hint}
              </p>
            </div>
          ) : null}
        </section>

        {beside ? (
          <aside className="flex w-[340px] shrink-0 flex-col border-l border-ink-100 bg-ink-50">
            <AssistantPanel assistant={assistant} onCollapse={() => collapse(true)} />
          </aside>
        ) : null}
      </div>

      {field && sheet && assistant ? (
        <Sheet onClose={() => setSheet(false)} header={<AssistantHeading />} size="tall" scrollBody={false}>
          <div className="flex min-h-0 flex-1 flex-col bg-ink-50">
            <AssistantPanel assistant={assistant} />
          </div>
        </Sheet>
      ) : null}
    </Modal>
  )
}

function AssistantHeading() {
  return (
    <span className="flex items-center gap-2 text-[15px] font-semibold text-ink-900">
      <AssistantAvatar />
      Assistant
    </span>
  )
}
